/**
 * 景點建立 / 編輯（F-3 / AC-F3-1~5）。
 *
 * 對應 BE：
 *   POST   /spots               建立
 *   PATCH  /spots/:id           編輯
 *   POST   /spots/:id/publish   發布
 *   POST   /spots/:id/unpublish 下架
 *   DELETE /spots/:id           軟刪除
 *
 * ## 為什麼分頁
 *
 * 一個景點滿載會產出船上 637 個檔案，對應到這頁就是 6 個文字欄位 × 3 語系、
 * 4 個媒體插槽 × 3 語系、20 筆推薦點 × 9 個欄位 × 3 語系。全部平鋪在一頁沒有
 * 人填得完——不是欄位太多，而是看不出「我現在在填哪一個畫面」。
 *
 * 分頁的單位刻意是**船上的畫面**而不是資料表或檔名：填「一階畫面」的人心裡想的
 * 是那塊玻璃上會出現什麼，不是 `1_str2.txt`。檔名仍標在每個欄位旁邊，但只作為
 * 對照，不是導航。
 *
 * 語系是與畫面正交的另一個維度，故獨立成第二排頁籤——併成一排會變成 12 個頁籤。
 *
 * ## 兩種儲存語意並存
 *
 * 文字欄位隨「儲存變更」一起送（PATCH 整包），媒體插槽與推薦點是即時儲存（各自
 * 獨立 API）。兩者在畫面上都有標註——混淆的代價是「我剛改的到底存了沒」。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { SpotBasicFields } from '../components/SpotBasicFields';
import { SpotContentMatrix } from '../components/SpotContentMatrix';
import {
  displayFormFromSpot,
  displayFormToDto,
  SpotDisplayFields,
  type DisplayField,
} from '../components/SpotDisplayFields';
import { SpotDisplayPreview } from '../components/SpotDisplayPreview';
import { SpotFolderApply } from '../components/SpotFolderApply';
import { SpotMediaFields } from '../components/SpotMediaFields';
import { SpotPoiFields } from '../components/SpotPoiFields';
import { SpotTranslatePanel, type AppliedTranslation } from '../components/SpotTranslatePanel';
import {
  cleanDescriptionI18n,
  cleanNameI18n,
  emptySpotForm,
  LOCALE_LABEL,
  LOCALES,
  localeHasContent,
  spotToForm,
  validateSpotForm,
  type Locale,
  type SpotFormState,
} from '../components/spot-form';
import { clearAllPoiMarks } from '../components/spot-poi-translation';
import {
  buildTranslationStatePatch,
  clearAllPatch,
  draftKey,
  formKeyOf,
  type MtDraftMap,
} from '../components/spot-translation';
import {
  createSpot,
  deleteSpot,
  getSpot,
  listSpotPois,
  publishSpot,
  unpublishSpot,
  updateSpot,
  upsertSpotPoi,
  type SpotMediaSlot,
  type SpotPoiPatch,
} from '../lib/api-client';

/** 推薦點的機翻記號 patch；`clearAllPoiMarks` 的回傳型別。 */
type SpotPoiTranslationPatch = NonNullable<SpotPoiPatch['translation_state']>;

/**
 * 內容分頁。
 *
 * `fields` / `slots` 決定該頁顯示哪些欄位與插槽——分法照船上的畫面層級：一階是
 * 靠近時跳出的卡片，二階是點進去的詳細頁，推薦點是再往下的店家清單。
 */
interface TabSpec {
  key: string;
  label: string;
  hint?: string;
  fields?: readonly DisplayField[];
  slots?: readonly SpotMediaSlot[];
  /** 內容不分語系；此分頁不顯示語系頁籤（顯示了會讓人以為切換有作用）。 */
  crossLocale?: boolean;
}

const TABS: readonly TabSpec[] = [
  {
    key: 'overview',
    label: '總覽',
    hint: '這個景點還缺什麼——三語系一起看，點格子跳到該欄位',
    crossLocale: true,
  },
  { key: 'basic', label: '基本資料', hint: '識別碼、名稱、描述與座標；與窗屏無關' },
  {
    key: 'level1',
    label: '一階畫面',
    hint: '船靠近景點時跳出的卡片：標題、描述、輪播圖與語音',
    fields: ['location_name', 'poi_title', 'poi_desc', 'intro'],
    slots: ['poi_image', 'intro_voice'],
  },
  {
    key: 'level2',
    label: '二階畫面',
    hint: '點進去的詳細頁：詳細標題、內文、影片與語音',
    fields: ['detail_title', 'detail_text'],
    slots: ['detail_video', 'detail_voice'],
  },
  { key: 'pois', label: '推薦點', hint: '附近店家，四個分類各最多 5 筆' },
  {
    key: 'translate',
    label: '翻譯',
    hint: '以繁中主稿產生英日草稿；採用後仍要按「儲存變更」才寫入',
    crossLocale: true,
  },
  {
    key: 'preview',
    label: '產出預覽',
    hint: '發布前先看會產生哪些船上檔案、還缺什麼（三語系一起看）',
    crossLocale: true,
  },
];

export function SpotEditPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const isEdit = id !== undefined && id !== 'new';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form, setForm] = useState<SpotFormState>(emptySpotForm);
  const [activeLocale, setActiveLocale] = useState<Locale>('zh-TW');
  /*
   * 編輯既有景點時預設停在總覽：接手一個已經有內容的景點，第一件事永遠是「現在
   * 是什麼狀態、還缺什麼」。新增時總覽無事可報（也還沒有 spot id 可查），從基本
   * 資料開始。
   */
  const [activeTab, setActiveTab] = useState<string>(isEdit ? 'overview' : 'basic');
  const [formError, setFormError] = useState<string | null>(null);
  /**
   * 這次套用過、還沒儲存的譯文：`欄位_i18n|語系` → 當初填進去的文字。
   *
   * 儲存時用它判斷「人到底有沒有動過這格」——只按套用就是機翻待校，改過字才算校對過。
   */
  const [mtDrafts, setMtDrafts] = useState<MtDraftMap>({});

  const spotQuery = useQuery({
    queryKey: ['spot', id],
    queryFn: () => getSpot(id as string),
    enabled: isEdit,
  });

  useEffect(() => {
    const s = spotQuery.data;
    if (!s) return;
    setForm(spotToForm(s, displayFormFromSpot(s)));
  }, [spotQuery.data]);

  /**
   * 把譯文填進表單。刻意不直接呼叫 API：寫入仍走「儲存變更」那一條路，
   * 使用者才有機會在送出前逐格看過。
   */
  const applyTranslations = (items: readonly AppliedTranslation[]): void => {
    setForm((prev) => {
      // 名稱與描述在表單頂層，窗屏六欄在 display 底下；三個地方都要能寫
      const next = {
        ...prev,
        name: { ...prev.name },
        description: { ...prev.description },
        display: { ...prev.display },
      };
      for (const it of items) {
        if (it.field === 'name_i18n') next.name[it.locale] = it.text;
        else if (it.field === 'description_i18n') next.description[it.locale] = it.text;
        else {
          const key = formKeyOf(it.field) as keyof typeof next.display;
          next.display[key] = { ...next.display[key], [it.locale]: it.text };
        }
      }
      return next;
    });
    setMtDrafts((prev) => {
      const next = { ...prev };
      for (const it of items) next[draftKey(it.field, it.locale)] = it.text;
      return next;
    });
  };

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['spots.list'] });
    if (isEdit) {
      void queryClient.invalidateQueries({ queryKey: ['spot', id] });
      // 預覽是「若現在發布會產生什麼」，任何內容變更都會改變答案。
      // （切到預覽分頁本來就會重新請求——元件是條件渲染的——這行是為了在
      // 已經停在預覽分頁時儲存也能立刻反映。）
      void queryClient.invalidateQueries({ queryKey: ['spot.display-preview', id] });
    }
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const message = validateSpotForm(form, isEdit);
      if (message) throw new Error(message);

      const nameI18n = cleanNameI18n(form.name) as Record<string, string>;
      const descI18n = cleanDescriptionI18n(form.description);
      // roi_id：空字串代表「不納入窗屏導覽」。編輯時送 null 才能清空既有值；
      // 新增時送 undefined（欄位不出現）——後端對 create 不接受 null。
      const roiRaw = form.roi_id.trim();
      const roiId = roiRaw === '' ? null : Number(roiRaw);
      const displayDto = displayFormToDto(form.display);

      if (isEdit) {
        /*
         * 機翻記號跟著同一次 PATCH 送：分兩次請求會出現「文字存了、記號沒存」的
         * 中間狀態，而那個狀態看起來就是「有人校對過了」。
         */
        const translationState = buildTranslationStatePatch({
          form,
          drafts: mtDrafts,
          serverState: spotQuery.data?.translation_state ?? {},
          serverValues: (spotQuery.data ?? {}) as Record<
            string,
            Record<string, string> | null | undefined
          >,
        });
        return updateSpot(id as string, {
          name_i18n: nameI18n,
          description_i18n: descI18n,
          latitude: Number(form.latitude),
          longitude: Number(form.longitude),
          trigger_radius_m: Number(form.trigger_radius_m),
          roi_id: roiId,
          ...(translationState ? { translation_state: translationState } : {}),
          ...displayDto,
        });
      }
      return createSpot({
        spot_code: form.spot_code.trim(),
        name_i18n: nameI18n,
        description_i18n: descI18n,
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
        trigger_radius_m: Number(form.trigger_radius_m),
        ...(roiId === null ? {} : { roi_id: roiId }),
        ...displayDto,
      });
    },
    onSuccess: (spot) => {
      // 記號已經寫進伺服器，本地那份暫存就沒有意義了；留著會在下次儲存時
      // 拿舊譯文去比對現在的內容，得到相反的結論
      setMtDrafts({});
      invalidate();
      navigate(`/spots/${spot.id}`);
    },
  });

  /**
   * 「全部標記已校對」——只動記號不動文字。
   *
   * 譯文本身正確、人看過就好的情況下，逼使用者去每個欄位改一個字才能解除記號
   * 是在懲罰正確的翻譯。
   */
  const clearMtMutation = useMutation({
    mutationFn: async () => {
      // 記號存在兩個地方（景點一份、每筆推薦點各一份），但畫面上只有一顆按鈕——
      // 只清一半會讓橫幅的數字降下來卻不歸零，看起來像按鈕壞了
      const patch = clearAllPatch(spotQuery.data?.translation_state ?? {});
      const pois = Object.values((await listSpotPois(id as string)).categories ?? {}).flat();
      const poiTargets = pois
        .map((poi) => ({ poi, marks: clearAllPoiMarks(poi) }))
        .filter((t): t is { poi: (typeof pois)[number]; marks: SpotPoiTranslationPatch } =>
          Boolean(t.marks),
        );

      if (!patch && poiTargets.length === 0) throw new Error('沒有需要解除的機翻記號');
      if (patch) await updateSpot(id as string, { translation_state: patch });
      for (const t of poiTargets) {
        await upsertSpotPoi(id as string, t.poi.category, t.poi.seq, {
          translation_state: t.marks,
        });
      }
    },
    onSuccess: () => {
      invalidate();
      void queryClient.invalidateQueries({ queryKey: ['spot.pois', id] });
    },
  });

  const publishMutation = useMutation({
    mutationFn: (publish: boolean) =>
      publish ? publishSpot(id as string) : unpublishSpot(id as string),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteSpot(id as string),
    onSuccess: () => {
      invalidate();
      navigate('/spots');
    },
  });

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    setFormError(null);
    saveMutation.mutate(undefined, {
      onError: (err) => setFormError((err as Error).message),
    });
  };

  const spot = spotQuery.data;
  const tab = TABS.find((t) => t.key === activeTab) ?? TABS[0]!;
  const localeLabel = LOCALE_LABEL[activeLocale];

  return (
    <div>
      <div className="page-head">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h1 className="page-head__title">{isEdit ? '編輯景點' : '新增景點'}</h1>
          {spot && (
            <span
              className={`badge ${spot.deleted_at ? 'danger' : spot.is_published ? 'success' : 'warning'}`}
            >
              {spot.deleted_at ? '已刪除' : spot.is_published ? '已發布' : '草稿'}
            </span>
          )}
        </div>
        <div className="page-head__actions">
          <button onClick={() => navigate('/spots')}>← 返回清單</button>
        </div>
      </div>

      {isEdit && spotQuery.isLoading && <div>載入中…</div>}
      {isEdit && spotQuery.error && (
        <div className="error-banner">{(spotQuery.error as Error).message}</div>
      )}

      {/*
        寬度跟著頁面走。先前固定 820px，在寬螢幕上右側整片空白，而「推薦點」與
        「產出預覽」兩個分頁最吃寬度——卡片排不開、檔案樹與 §15 JSON 被擠成一
        條。單行輸入框不會跟著拉長，那個上限在 `Field` 與 `SpotDisplayFields`
        各自的欄位包裝上。
      */}
      <form
        onSubmit={handleSubmit}
        style={{
          background: '#fff',
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          padding: 16,
          margin: '12px 0',
        }}
      >
        {/*
          資料夾套用放在分頁列之上：它一次填滿三個窗屏分頁的所有欄位，擺在某個
          分頁裡會讓人先手動填完別的分頁才發現有這顆按鈕。
        */}
        {isEdit && (
          <SpotFolderApply
            spotId={id as string}
            roiId={form.roi_id}
            onApplied={() => {
              invalidate();
              void queryClient.invalidateQueries({ queryKey: ['spot.media', id] });
              void queryClient.invalidateQueries({ queryKey: ['spot.pois', id] });
            }}
          />
        )}

        {/* 內容分頁：單位是船上的畫面 */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb' }}>
          {TABS.map((t) => {
            const active = t.key === activeTab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                style={{
                  padding: '10px 16px',
                  border: 'none',
                  borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
                  background: 'none',
                  color: active ? '#2563eb' : '#6b7280',
                  fontWeight: active ? 600 : 400,
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                {t.label}
              </button>
            );
          })}
        </div>

        {/* 語系：與畫面正交的另一個維度 */}
        <div
          style={{
            display: tab.crossLocale ? 'none' : 'flex',
            alignItems: 'center',
            gap: 4,
            margin: '8px 0 4px',
            fontSize: 13,
          }}
        >
          <span style={{ color: '#9ca3af', marginRight: 4 }}>語系</span>
          {LOCALES.map((l) => {
            const active = l === activeLocale;
            return (
              <button
                key={l}
                type="button"
                onClick={() => setActiveLocale(l)}
                style={{
                  padding: '4px 10px',
                  border: `1px solid ${active ? '#2563eb' : '#e5e7eb'}`,
                  borderRadius: 4,
                  background: active ? '#eff6ff' : '#fff',
                  color: active ? '#2563eb' : '#6b7280',
                  fontWeight: active ? 600 : 400,
                  cursor: 'pointer',
                  fontSize: 12,
                }}
              >
                {LOCALE_LABEL[l]}
                {localeHasContent(form, l) && <span style={{ color: '#10b981' }}> ●</span>}
              </button>
            );
          })}
        </div>

        {tab.hint && (
          <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 12px' }}>{tab.hint}</p>
        )}

        {activeTab === 'overview' &&
          (isEdit ? (
            <SpotContentMatrix
              spotId={id as string}
              form={form}
              translationState={spot?.translation_state ?? {}}
              onJump={(tab, locale) => {
                setActiveTab(tab);
                setActiveLocale(locale);
              }}
              onClearMt={() => clearMtMutation.mutate()}
              clearingMt={clearMtMutation.isPending}
            />
          ) : (
            <p style={{ fontSize: 12, color: '#9ca3af', margin: '12px 0' }}>
              建立景點後即可在此檢視內容覆蓋率。
            </p>
          ))}

        {activeTab === 'basic' && (
          <SpotBasicFields
            form={form}
            setForm={setForm}
            activeLocale={activeLocale}
            isEdit={isEdit}
          />
        )}

        {tab.fields && (
          <SpotDisplayFields
            value={form.display}
            onChange={(display) => setForm((p) => ({ ...p, display }))}
            locale={activeLocale}
            localeLabel={localeLabel}
            title={`${tab.label}文字`}
            only={tab.fields}
          />
        )}

        {/* 媒體掛載走獨立 API 立即生效，新增景點時還沒有 id 可掛 */}
        {tab.slots &&
          (isEdit ? (
            <SpotMediaFields
              spotId={id as string}
              cmsLocale={activeLocale}
              localeLabel={localeLabel}
              roiId={form.roi_id}
              title={`${tab.label}圖片與影音`}
              only={tab.slots}
            />
          ) : (
            <p style={{ fontSize: 12, color: '#9ca3af', margin: '12px 0' }}>
              圖片與影音可於景點建立後掛載。
            </p>
          ))}

        {activeTab === 'pois' &&
          (isEdit ? (
            <SpotPoiFields
              spotId={id as string}
              locale={activeLocale}
              localeLabel={localeLabel}
              roiId={form.roi_id}
            />
          ) : (
            <p style={{ fontSize: 12, color: '#9ca3af', margin: '12px 0' }}>
              推薦點可於景點建立後新增。
            </p>
          ))}

        {activeTab === 'translate' &&
          (isEdit ? (
            <SpotTranslatePanel spotId={id as string} onApply={applyTranslations} />
          ) : (
            <p style={{ fontSize: 12, color: '#9ca3af', margin: '12px 0' }}>
              建立景點並填好中文之後，即可在此產生英日翻譯建議。
            </p>
          ))}

        {activeTab === 'preview' &&
          (isEdit ? (
            <SpotDisplayPreview spotId={id as string} />
          ) : (
            <p style={{ fontSize: 12, color: '#9ca3af', margin: '12px 0' }}>
              建立景點後即可預覽產出。
            </p>
          ))}

        {formError && <div className="error-banner">{formError}</div>}
        {saveMutation.error && !formError && (
          <div className="error-banner">{(saveMutation.error as Error).message}</div>
        )}

        {/*
          儲存按鈕在所有分頁都在：表單狀態是跨分頁共用的一份，只在「基本資料」
          出現會讓人以為別的分頁不用存。
        */}
        <div
          style={{
            marginTop: 12,
            paddingTop: 12,
            borderTop: '1px solid #f3f4f6',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <button type="submit" className="primary" disabled={saveMutation.isPending}>
            {saveMutation.isPending ? '儲存中…' : isEdit ? '儲存變更' : '建立景點'}
          </button>
          <span style={{ fontSize: 12, color: '#9ca3af' }}>
            文字欄位需按此儲存；圖片、影音與推薦點已即時生效
          </span>
        </div>
      </form>

      {/* 發布 / 下架 / 刪除 — 僅編輯模式 */}
      {isEdit && spot && !spot.deleted_at && (
        <section
          style={{
            background: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            padding: 16,
            margin: '12px 0',
          }}
        >
          <h2 style={{ marginTop: 0, fontSize: 16 }}>發布狀態</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            {spot.is_published ? (
              <button
                onClick={() => publishMutation.mutate(false)}
                disabled={publishMutation.isPending}
              >
                下架
              </button>
            ) : (
              <button
                onClick={() => publishMutation.mutate(true)}
                disabled={publishMutation.isPending}
              >
                發布
              </button>
            )}
            <button
              onClick={() => {
                if (window.confirm(`確定軟刪除景點「${spot.spot_code}」？民眾端將不再顯示。`)) {
                  deleteMutation.mutate();
                }
              }}
              disabled={deleteMutation.isPending}
            >
              軟刪除
            </button>
          </div>
          {publishMutation.error && (
            <div className="error-banner">{(publishMutation.error as Error).message}</div>
          )}
          {deleteMutation.error && (
            <div className="error-banner">{(deleteMutation.error as Error).message}</div>
          )}
        </section>
      )}
    </div>
  );
}
